import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, skipWhile, throwError } from 'rxjs';
import { IdentityService } from '../services/identity.service';
import { LoadingService } from '../core/loading-overlay/loading.service';
import { Identity } from '../interfaces/identity';
import { catchError, finalize, first, switchMap } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private identityService = inject(IdentityService);
  private loadingService = inject(LoadingService);
  private isRefreshTokenInProgress = false;
  private actualIdentity$: BehaviorSubject<Identity | null> = new BehaviorSubject<Identity | null>(null);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const accessToken = this.identityService.token;

    if (!req.url.includes('telemetry')) {
      this.loadingService.run();
    }

    if (!accessToken) {
      return next.handle(this.addRequestMetadata(req)).pipe(
        finalize(() => this.loadingService.stop()),
        catchError(err => throwError(err)),
      );
    }

    return next.handle(this.addRequestMetadata(req, accessToken)).pipe(
      finalize(() => this.loadingService.stop()),
      catchError((err: HttpErrorResponse) => {
        if (req.url.includes('refreshaccesstoken') && err instanceof HttpErrorResponse && err.status === 401) {
          this.identityService.logout();

          return throwError(err);
        }

        if (err && err.status === 401) {
          return this.handleExpiredTokenError(req, next);
        }

        return throwError(err);
      }),
    );
  }

  private addRequestMetadata(req: HttpRequest<any>, token: string = null): HttpRequest<any> {
    const companySlug: string = this.identityService.companySlug;
    const isCompanyRequest: boolean = req.url.includes(`companies/${companySlug}`);

    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (isCompanyRequest) {
      headers['X-Tenant-ID'] = companySlug;
    }

    const updatedParams: HttpParams = this.identityService.getIdentityRequestParams(req);

    return req.clone({
      setHeaders: headers,
      params: updatedParams,
    });
  }

  private handleExpiredTokenError(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshTokenInProgress) {
      this.isRefreshTokenInProgress = true;
      this.actualIdentity$.next(null);

      return this.identityService.refreshAccessToken().pipe(
        catchError(() => {
          this.identityService.logout();

          return EMPTY;
        }),
        switchMap((identity: Identity) => {
          this.identityService.identity = identity;
          this.actualIdentity$.next(identity);

          return next.handle(this.addRequestMetadata(req, this.identityService.token));
        }),
        finalize(() => (this.isRefreshTokenInProgress = false)),
      );
    }

    return this.actualIdentity$.pipe(
      skipWhile(identity => identity === null),
      first(),
      switchMap(identity => next.handle(this.addRequestMetadata(req, identity.AccessToken))),
    );
  }
}
